import { createContext, useContext, useEffect } from "react";
import type { ThemePreference } from "@/lib/theme-preference";

interface ThemeContextValue {
  theme: "light" | "dark";
  toggleTheme: () => void;
  setTheme: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Retain the context API for embedded/consumer callers, but do not expose a
// way to turn dark mode back on or persist a new dark preference.
const daytimeTheme: ThemeContextValue = {
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // The admin shell owns its separate theme and persistence.
    if (document.querySelector(".admin")) return;
    document.documentElement.classList.remove("dark");
    document.documentElement.style.colorScheme = "light";
  }, []);

  return (
    <ThemeContext.Provider value={daytimeTheme}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
